import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, Spinner, Table, UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";
import { urlEndPoint } from "helpers/GeneralHelper";
import { submitLeadsAddedStatusCandidatesFilterData } from "model/LeadsAddedReportModel";

const resolveCallDetailsHref = (row = {}) => {
    const candidateClientId = String(row?.candidateClientId ?? "").trim();
    return candidateClientId ? `/${urlEndPoint.callDetails}/${candidateClientId}` : "";
};

const mapCandidateRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        id: item?.candidateClientId ?? `candidate-${index}`,
        candidateClientId: String(item?.candidateClientId ?? "").trim(),
        candidate:
            item?.fullName ??
            item?.candidate ??
            item?.candidateName ??
            item?.name ??
            "",
        email: item?.email ?? item?.emailAddress ?? "",
        phone: item?.phone ?? item?.phoneNumber ?? item?.mobile ?? "",
    }));

const extractCandidateRows = (response = {}) => {
    const responseData = response?.data ?? response ?? {};

    if (Array.isArray(responseData)) return responseData;

    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    if (Array.isArray(nestedData)) return nestedData;
    if (Array.isArray(nestedData?.rows)) return nestedData.rows;
    if (Array.isArray(nestedData?.records)) return nestedData.records;
    if (Array.isArray(nestedData?.data)) return nestedData.data;
    if (Array.isArray(responseData?.rows)) return responseData.rows;
    if (Array.isArray(responseData?.records)) return responseData.records;
    return [];
};

const LeadsAddedStatusCandidatesModal = ({
    isOpen,
    onClose,
    clientId = "",
    clientName = "",
    statusKey = "",
    statusLabel = "",
    filterPayload = {},
}) => {
    const [loading, setLoading] = useState(false);
    const [candidateRows, setCandidateRows] = useState([]);

    const modalTitle = useMemo(() => {
        const client = String(clientName || "").trim() || "Client";
        const status = String(statusLabel || statusKey || "").trim() || "Status";
        return `${client} > ${status}`;
    }, [clientName, statusKey, statusLabel]);

    useEffect(() => {
        if (!isOpen) {
            setCandidateRows([]);
            return;
        }

        const normalizedClientId = String(clientId || "").trim();
        const normalizedStatusKey = String(statusKey || "").trim();
        if (!normalizedClientId || !normalizedStatusKey) {
            setCandidateRows([]);
            return;
        }

        let cancelled = false;

        const loadCandidates = async () => {
            setLoading(true);
            try {
                const response = await submitLeadsAddedStatusCandidatesFilterData({
                    clientId: normalizedClientId,
                    statusKey: normalizedStatusKey,
                    jobCategoryIds: filterPayload?.jobCategoryIds ?? null,
                    dateFrom: filterPayload?.dateFrom ?? null,
                    dateTo: filterPayload?.dateTo ?? null,
                });

                if (cancelled) return;

                if (response == null || response?.success === false) {
                    setCandidateRows([]);
                    if (response?.message) {
                        toast.error(response.message);
                    }
                    return;
                }

                setCandidateRows(mapCandidateRows(extractCandidateRows(response)));
            } catch (error) {
                if (!cancelled) {
                    toast.error(error?.message || "Unable to load candidates");
                    setCandidateRows([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void loadCandidates();

        return () => {
            cancelled = true;
        };
    }, [isOpen, clientId, statusKey, filterPayload]);

    const closeModal = () => {
        if (!loading) {
            onClose?.();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={closeModal}
            backdrop="static"
            centered
            size="lg"
            className="modal-full-height"
        >
            <ModalBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-uppercase">{modalTitle}</h5>
                    <Button close type="button" onClick={closeModal} disabled={loading} />
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table bordered className="align-middle mb-0 leads-added-status-candidates-table">
                            <thead>
                                <tr>
                                    <th>Candidate</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th className="text-center" style={{ width: "72px" }}>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidateRows.length ? (
                                    candidateRows.map((row) => {
                                        const detailsHref = resolveCallDetailsHref(row);
                                        const callActionId = `leads-added-call-details-${row.id}`;

                                        return (
                                            <tr key={row.id}>
                                                <td>{row.candidate || "—"}</td>
                                                <td>{row.email || "—"}</td>
                                                <td>{row.phone || "—"}</td>
                                                <td className="text-center">
                                                    {detailsHref ? (
                                                        <div className="d-inline-flex align-items-center justify-content-center">
                                                            <Link
                                                                to={detailsHref}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                                                                id={callActionId}
                                                                aria-label="Call details"
                                                            >
                                                                <i
                                                                    className="mdi mdi-phone-log text-success"
                                                                    aria-hidden
                                                                />
                                                            </Link>
                                                            <UncontrolledTooltip target={callActionId}>
                                                                Call details
                                                            </UncontrolledTooltip>
                                                        </div>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">
                                            No candidates found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

LeadsAddedStatusCandidatesModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clientName: PropTypes.string,
    statusKey: PropTypes.string,
    statusLabel: PropTypes.string,
    filterPayload: PropTypes.object,
};

export default LeadsAddedStatusCandidatesModal;
